export enum LogLevel {
  Error = 'error',
  Warn = 'warn',
  Info = 'info',
  Debug = 'debug',
}

export interface ILog {
  args: any[];
  logLevel: LogLevel;
  timestamp: number;
}

type LoggingFunction = (details: string, meta?: any) => void;

export class Logger {
  public readonly debug: LoggingFunction;
  public readonly error: LoggingFunction;
  public readonly info: LoggingFunction;
  public readonly warn: LoggingFunction;

  private readonly disablePrint: Set<LogLevel>;
  private readonly logSize: number;
  private logs: ILog[] = [];

  public constructor({
    disablePrint = new Set(),
    logSize = Infinity,
  }: {
    disablePrint?: Set<LogLevel>,
    logSize?: number,
  }) {
    this.disablePrint = disablePrint;
    this.logSize = logSize;

    this.debug = this.log(LogLevel.Debug);
    this.error = this.log(LogLevel.Error);
    this.info  = this.log(LogLevel.Info);
    this.warn  = this.log(LogLevel.Warn);
  }

  public readonly getLogs = () => this.logs;

  private log = (logLevel: LogLevel) => (...args: any[]) => {
    try {
      if (!this.disablePrint.has(logLevel)) {
        // TODO: Consider overwriting the native console.log/etc. methods so that they call log()
        // instead. This would allow us to persist messages logged by other libraries that we use,
        // if they log any messages through console.log/etc.
        // eslint-disable-next-line no-console
        console[logLevel](...args);
      }

      const timestamp = Date.now();
      this.logs.unshift({ args, logLevel, timestamp });

      if (this.logs.length > this.logSize) {
        this.logs.splice(this.logSize - 1, this.logs.length - this.logSize);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };
}

const EXCLUDE_LOG_LEVELS = new Set<LogLevel>([]);
const PERSIST_LOG_COUNT = 100;

// Show info and debug when not in production mode.
if (process.env.NODE_ENV === 'production') {
  EXCLUDE_LOG_LEVELS.add(LogLevel.Info);
  EXCLUDE_LOG_LEVELS.add(LogLevel.Debug);
}

const logger = new Logger({
  disablePrint: EXCLUDE_LOG_LEVELS,
  logSize: PERSIST_LOG_COUNT,
});

const { error, warn, info, debug, getLogs } = logger;
export { error, warn, info, debug, getLogs };

export default logger;
